import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { ChartBarHorizontalCustom, Legend } from '../../../../components'
import { LEGEND_COLOR_MAP, GRAPH_COLOR_PALLET2, GRAPH_COLOR_PALLET_V2, GRAPH_COLOR_PALLET_V2_TEXT, LEGEND_COLOR_MAP_NEW, LEGEND_COLOR_MAP_V2 } from '../../../../constants'
import {
  BarWrap,
  BarWrapper,
  ContentWrap,
  LegendText,
  LegendWrap,
  RowBar,
  SegmentRow,
  SegmentRowContent,
  SegmentWrap,
  StyledTableHead,
  TableData,
  TableWrapper,
  TdWrapper,
  ToolTipColWrapper,
  TooltipRowName,
  ToolTipContainer,
  ToolTipWrapper,
  ToolTipColName,
  TitleCell,
  SliderWrap,
  TitleWrap
} from './style'
import { getObjectAsArray, hexToRgba } from '../../../../util'
import { Typography } from '@mui/material'
import TableWithSlider from './tableWithSlider'
import Slider from 'react-slick'
import { NextArrow, PrevArrow } from '../reportLandScapeCompetitiveBrandImagery/landScapeHeader'

// Logic for opacity and text color
export const getBackgroundColorOpacityAndTextColor = (color, elementPercentage, name='') => {
  let opacity = 0;
  if (elementPercentage === 0) {
    opacity = 10;
  } else if (elementPercentage >= 1 && elementPercentage <= 9) {
    opacity = elementPercentage + 20;
  } else if (elementPercentage >= 10 && elementPercentage <= 69) {
    opacity = elementPercentage + 30;
  } else {
    opacity = 100;
  }

  const rgbaColor = hexToRgba(color, opacity);
  let finalObject = {
      color: rgbaColor,
      textColor: '#ffffff'
    };

  if (opacity >= 0 && opacity <= 59) {
    finalObject = {
      color: rgbaColor,
      textColor: '#000000'
    }
  }
  return LEGEND_COLOR_MAP_V2?.[name] ? {color: LEGEND_COLOR_MAP_V2[name], textColor: '#ffffff'} : finalObject
}

// simple transpose utility
const transpose = (matrix) => {
  if (!Array.isArray(matrix) || !matrix.length) return [];
  return matrix[0].map((_, colIdx) => matrix.map(row => row[colIdx]));
};

export const getTableData = (data, flip = false) => {
  try {
    let tableData = [];
    let headerRow = [{ value: "Driver", show: true }];

    for (let i of data) {
      if (i?.show !== false) {
        headerRow.push({ value: i?.PrimarySegmentName, key:i?.PrimarySegmentName, show: true });
      }
    }
    tableData.push(headerRow);

    const driversMap = new Map();
    for (let i of data) {
      const key = i?.PrimarySegmentName
      for (let j of getObjectAsArray(i?.SecondarySegmentSet?.SecondarySegment)) {
        if (!j?.show) continue;

        if (!driversMap.has(j?.SegmentName)) {
          driversMap.set(j?.SegmentName, [{ value: j?.SegmentName+'', key: j.SegmentName, show: j?.show }]);
        }
        driversMap.get(j?.SegmentName).push({ value: j?.SegmentValue, key: j.SegmentName, show: j?.show });
      }
    }

    for (let row of driversMap.values()) {
      tableData.push(row);
    }

    if (flip) {
      const plain = tableData.map(r => r.map(c => c?.value ?? ""));
      const flipped = transpose(plain);
      return flipped.map(r => r.map(v => ({ value: v, show: true })));
    }

    return tableData;
  } catch (error) {
    console.error(error);
    return [];
  }
};


const BuyerTable = ({
  view,
  data,
  isExtraColor = false,
  colorPallet = [],
  showLegendToolTip = false,
  allValuesShow,
  sortedPieColorObject
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, width: 0, arrowLeft: true});
  const tooltipRef = useRef();
  // const [tooltipContainerHeight, setTooltipContainerHeight] = useState(0);
  const [tooltipData, setTooltipData] = useState({
    rowName: '',
    colName: '',
    itemPercentage: 0,
    itemColor: 'transparent'
  });
  const rowSlidersRef = useRef([]);
  const legendSliderRef = useRef(null);
  const segmentLabelRef = useRef({});
  const [segmentLabelHeights, setSegmentLabelHeights] = useState({});
  const titleCellRef = useRef([]);
  const [titleCellHeights, setTitleCellHeights] = useState({});

  const registerRowSlider = useCallback((el, index) => {
    if (el) {
      rowSlidersRef.current[index] = el;
    }
  }, []);

  useEffect(() => {
    const heights = {};
    Object.keys(segmentLabelRef.current).forEach((key) => {
      if (segmentLabelRef.current[key]) {
        heights[key] = segmentLabelRef.current[key].offsetHeight;
      }
    });
    setSegmentLabelHeights(heights);
  }, [data]);

  useEffect(() => {
    const titleHeights = {};
    titleCellRef.current.forEach((el, index) => {
      if (el) {
        titleHeights[index] = el.offsetHeight;
      }
    });
    setTitleCellHeights(titleHeights);
  }, [data]);

  const handleNext = useCallback(() => {
    rowSlidersRef.current.forEach(slider => {
      if (slider?.innerSlider) {
        const { currentSlide, slideCount } = slider.innerSlider.state;
        if (currentSlide < slideCount - slider.props.slidesToShow) {
          slider.slickNext();
        }
      }
    });

    if (legendSliderRef.current?.innerSlider) {
      const { currentSlide, slideCount } = legendSliderRef.current.innerSlider.state;
      if (currentSlide < slideCount - legendSliderRef.current.props.slidesToShow) {
        legendSliderRef.current.slickNext();
      }
    }
  }, []);

  const handlePrev = useCallback(() => {
    rowSlidersRef.current.forEach(slider => {
      if (slider?.innerSlider?.state.currentSlide > 0) {
        slider.slickPrev();
      }
    });

    if (legendSliderRef.current?.innerSlider?.state.currentSlide > 0) {
      legendSliderRef.current.slickPrev();
    }
  }, []);

  const defaultCarouselSettings = {
    dots: false,
    infinite: false,
    slidesToShow: 6,
    slidesToScroll: 1,
    vertical: false,
    verticalSwiping: false,
    swipeToSlide: false,
    draggable: false,
    touchMove: false,
    initialSlide: 0,
    speed: 0,
    arrows: false,
  }

  const defaultLegendsSettings = {
    dots: false,
    infinite: false,
    slidesToShow: 6,
    slidesToScroll: 1,
    vertical: false,
    verticalSwiping: false,
    swipeToSlide: false,
    draggable: false,
    touchMove: false,
    initialSlide: 0,
    speed: 0,
    nextArrow: <NextArrow onExtraClick={handleNext} />,
    prevArrow: <PrevArrow onExtraClick={handlePrev} />,
  }

  // Tooltip handlers
  const handleCellMouseEnter = useCallback((rowName, colName, element, itemPercentage, itemColor) => {
    if (element) {
      const rect = element.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current?.offsetWidth || 150; // approx width fallback

      let xPosition = rect.right + 105; // default right side
      let yPosition = rect.top + 15;
      let arrowLeft = true;

      // // agar right side screen ke bahar ja raha hai to left side me dikhana
      if (rect.right + 20 + tooltipWidth > window.innerWidth) {
        xPosition = rect.left - 105;
        arrowLeft = false;
      }

      setTooltipPosition({
        x: xPosition,
        y: yPosition,
        // width: rect.width - 10,
        width: rect.width,
        arrowLeft
      });
    }

    setTooltipData({ itemPercentage, rowName, colName, itemColor });
    setIsTooltipVisible(true);

    // setTimeout(() => {
      // setTooltipContainerHeight(tooltipRef.current?.offsetHeight);
    // });
  }, [])

  const handleCellMouseLeave = useCallback(() => {
    setIsTooltipVisible(false);
    // setTooltipData({ itemPercentage: 0, rowName: '', colName: '', itemColor: 'transparent' });
  
  }, [])

  const colors = useMemo(() => {
    if (colorPallet.length > 0) return colorPallet;
    return isExtraColor ? [...GRAPH_COLOR_PALLET_V2, ...GRAPH_COLOR_PALLET_V2_TEXT, ...GRAPH_COLOR_PALLET_V2] : LEGEND_COLOR_MAP;
  }, [isExtraColor, colorPallet]);

  const tableData = useMemo(() => getTableData(data), [data]);
  const maxValue = tableData.length > 1 ? Math.max(...tableData.slice(1).flatMap(row => row.slice(1).map(cell => Number(cell.value) || 0))) : 100;
  
  const dataLength = useMemo(() => {
    const visible = getObjectAsArray(data[0]?.SecondarySegmentSet?.SecondarySegment).filter(d => d?.show !== false);
    return visible?.length || 0;
  }, [data]);

  // Settings moved to useMemo to prevent prop-change re-renders in Slick
  const carouselSettings = useMemo(() => ({
    dots: false,
    infinite: false,
    slidesToShow: 6,
    slidesToScroll: 1,
    speed: 0,
    arrows: false,
    draggable: false,
  }), []);

  return (
    <>
      {view === 'graph' && (
        <>
          {!!data?.length && (
            <ContentWrap className={`${dataLength > 10 ? 'slider-init' : ''}`}>
              {isTooltipVisible && (
                <ToolTipContainer
                  ref={tooltipRef}
                  arrow={tooltipPosition.arrowLeft}
                  style={{
                    position: 'fixed',
                    left: `${tooltipPosition.x}px`,
                    // top: `${tooltipPosition.y + (tooltipContainerHeight / 2)}px`,
                    top: `${tooltipPosition.y}px`,
                    transform: 'translate(-50%, -100%)',
                    zIndex: 7,
                    pointerEvents: 'none',
                    // paddingBottom: `${tooltipData.column ? 12 : 0}px`
                  }}
                >
                  <ToolTipWrapper>
                    <TooltipRowName>{tooltipData.rowName}</TooltipRowName>
                    <ToolTipColWrapper>
                      <ToolTipColName circleColor={tooltipData.itemColor}>{tooltipData.colName}</ToolTipColName>
                      <Typography fontWeight={600} fontSize='12px' color={tooltipData.itemColor}>{tooltipData.itemPercentage}%</Typography>
                    </ToolTipColWrapper>
                  </ToolTipWrapper>
                </ToolTipContainer>
              )}

              {dataLength > 10 ? (
                <SegmentWrap>
                  <TitleWrap>
                    {data.map((item, index) => (
                      <TitleCell 
                        ref={(el) => {titleCellRef.current[index] = el}}
                        style={{height: titleCellHeights[index] > 0 ? titleCellHeights[index] + 'px' : 'auto'}}
                        className='segmentLabel' 
                        key={index + item.PrimarySegmentName}
                      >
                        {item.PrimarySegmentName}
                      </TitleCell>
                    ))}
                  </TitleWrap>

                  {allValuesShow && (   
                  <SliderWrap>
                    {data.map((item, index) => {
                      const labelHeight = titleCellHeights[index] || 0;
                      const ht = labelHeight > 38 ? labelHeight + 'px' : '38px'
                      return (
                        <SegmentRow
                          style={{minHeight: ht}}
                          ht = {ht}
                          className='list segmentOfSegmentOverviewRow'
                          key={index + item.PrimarySegmentName}
                          data-age={item.PrimarySegmentName}
                        >
                          <SegmentRowContent>
                            <BarWrap className={`${item?.type?.toLowerCase()} ${dataLength > 10 ? 'slider-wrapper' : ''}`} style={{minHeight: ht}}>
                              <Slider
                                ref={(el) => registerRowSlider(el, index)}
                                // {...defaultCarouselSettings}
                                {...carouselSettings}
                                >

                                {getObjectAsArray(item?.SecondarySegmentSet?.SecondarySegment).map(({ SegmentName, SegmentValue, color, show }, i) => {
                                  if (!show) return;
                                  const isUndefined = SegmentValue === undefined || SegmentValue === null;
                                  const labelHeight = segmentLabelHeights[item.PrimarySegmentName] || 0;
                                  const colorAndOpacity = getBackgroundColorOpacityAndTextColor(color, SegmentValue, SegmentName);

                                  return (
                                  <RowBar
                                    onMouseEnter={(e) => handleCellMouseEnter(e.currentTarget.dataset.primary, SegmentName, e.currentTarget, SegmentValue, getBackgroundColorOpacityAndTextColor(colors[i], SegmentValue).color, SegmentName)}
                                    onMouseLeave={() => handleCellMouseLeave()}
                                    data-primary={item.PrimarySegmentName}
                                    colorAndOpacity={colorAndOpacity}
                                    style={{minHeight: ht}}
                                    visible={isUndefined ? 'hidden' : 'visible'}
                                    key={`${SegmentValue}${i}`}>
                                    {SegmentValue}%
                                  </RowBar>
                                )})}
                              </Slider>

                            </BarWrap>
                          </SegmentRowContent>
                        </SegmentRow>
                      )
                    })}

                    <BarWrap className={`legends ${dataLength > 10 ? 'slider-wrapper' : ''}`}>
                      <Slider ref={legendSliderRef} {...defaultLegendsSettings}>
                        {getObjectAsArray(data[0]?.SecondarySegmentSet?.SecondarySegment).map(({ SegmentName , show}, index) => {
                          if(!show) return;
                          return (
                            <LegendText key={index + SegmentName}>{SegmentName}</LegendText>
                          )
                        })}
                      </Slider>
                    </BarWrap>
                  </SliderWrap>
                  )}

                </SegmentWrap>
              ) : (
                <>
                    {data.map((item, index) => {
                      const labelHeight = segmentLabelHeights[item.PrimarySegmentName] || 0;
                      const ht = labelHeight > 38 ? labelHeight + 'px' : '38px'
                      return (
                        <SegmentRow
                          className={`list segmentOfSegmentOverviewRow ${!allValuesShow ? 'noVal' : ''}`}
                          key={index + item?.PrimarySegmentName}
                          data-age={item.PrimarySegmentName}
                        >
                          <span 
                            ref={(el) => {segmentLabelRef.current[item.PrimarySegmentName] = el}}
                            className='segmentLabel'
                          >
                            {item.PrimarySegmentName}
                          </span>
                          <SegmentRowContent>
                            <BarWrap className={item?.type?.toLowerCase()}>
                              {getObjectAsArray(item?.SecondarySegmentSet?.SecondarySegment).map((propData, i) => {
                                const { SegmentName, SegmentValue, clr, color, show } = propData

                                if (!show) return;
                                const isUndefined = SegmentValue === undefined || SegmentValue === null;
                                const colorAndOpacity = getBackgroundColorOpacityAndTextColor(clr, SegmentValue, SegmentName+'');

                                return (
                                  <RowBar
                                    onMouseEnter={(e) => handleCellMouseEnter(e.currentTarget.dataset.primary, SegmentName, e.currentTarget, SegmentValue, getBackgroundColorOpacityAndTextColor(colors[i], SegmentValue, SegmentName).color)}
                                    onMouseLeave={() => handleCellMouseLeave()}
                                    data-primary={item.PrimarySegmentName}
                                    colorAndOpacity={colorAndOpacity}
                                    style={{minHeight: ht}}
                                    visible={isUndefined ? 'hidden' : 'visible'}
                                    key={`${SegmentValue}${i}`}>
                                    {SegmentValue}%
                                  </RowBar>
                                )
                                })}
                            </BarWrap>
                          </SegmentRowContent>
                        </SegmentRow>
                      )
                    })}

                    <BarWrap className='legends'>
                      {getObjectAsArray(data[0]?.SecondarySegmentSet?.SecondarySegment).map(({ SegmentName , show}, index) => {
                        if (!show) return;
                        return (
                          <LegendText key={index + SegmentName}>{SegmentName}</LegendText>
                        )
                      })}
                    </BarWrap>
                </>
              )}


            </ContentWrap>)}
        </>
      )}

      {/*
      {view === 'table' && (
        <TableWrapper>
          {!!data?.length && tableData.length > 1 ? (
            <>
              <StyledTableHead>
                <TableRow>
                  {tableData[0].map((td, ind) => {
                    if (!td.show) return;
                    return (
                    <TableCell key={'th' + ind}>{td.value}</TableCell>
                  )})}
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {tableData.slice(1).map((row, rowIndex) => (
                  <TableRow key={'row' + rowIndex}>
                    {row.map((cell, cellIndex) => {
                      let w = 100 / row.length
                      if(w < 12) w = 12;
                      if (cellIndex === 0) w = '200px';
                      if (w === 12 && row.length > 8) w = '150px';
                      else w = w + '%';

                      if (!cell.show) return;
                      return (
                      <TableData key={'cell' + rowIndex} style={{minWidth: w, width: w}}>
                        <TdWrapper index={cellIndex} textColor={cellIndex !== 0 ? colors[rowIndex] : ''}>
                          <>
                            {cellIndex !== 0 ?
                              // <BarWrapper className='w62px' bgColor={colors[rowIndex]} width='62px' barWidth={cell.value / 100 * 62}>
                              <BarWrapper bgColor={colors[rowIndex]} width='calc(100% - 40px)' barWidth={(cell.value / maxValue) * 100}>
                                <div className='bar-filled'></div>
                              </BarWrapper>
                              : ''}

                            {cell.value}{cellIndex !== 0 ? '%' : ''} ###3
                          </>
                        </TdWrapper>
                      </TableData>
                    )})}
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <TableRow>
              <TableCell>
                <b>No Data</b>
              </TableCell>
            </TableRow>
          )}
        </TableWrapper>
      )}
      */}

      {view === 'table' && !!data?.length && tableData.length > 1 && (
        <TableWithSlider
          view={view}
          data={data}
          tableData={tableData}
          colors={colors}
          maxValue={maxValue}
          sortedPieColorObject={sortedPieColorObject}
        />
      )}
    </>
  )
}

export default BuyerTable




